/**
 * One longitude frame for a cyclone track. Polygons that cross the antimeridian are stored with
 * negative longitudes shifted by +360 (see geo.js), so a track that crosses it is drawn in that
 * same [0, 360) frame instead of jumping across the whole map.
 */
const isNum = (n) => Number.isFinite(n);

/** True when any consecutive pair of longitudes is more than 180 degrees apart. */
export function crossesAntimeridian(pairs) {
  return pairs.some(([a, b]) => isNum(a) && isNum(b) && Math.abs(a - b) > 180);
}

/**
 * Returns `toFrame(lng)` for a track. `pairs` is a list of [lngA, lngB] for connected points
 * (segments, raw segments, consecutive vertices).
 */
export function makeTrackFrame(pairs, forceShift = false) {
  const shift = forceShift || crossesAntimeridian(pairs);
  return (lng) => (shift && lng < 0 ? lng + 360 : lng);
}

/** Connected longitude pairs of a parsed track (segments, raw segments and vertex neighbours). */
export function trackLngPairs(track) {
  const pairs = [];
  (track?.segments || []).forEach((s) => pairs.push([s.from[0], s.to[0]]));
  (track?.rawSegments || []).forEach((s) => pairs.push([s.from[0], s.to[0]]));
  const v = track?.vertices || [];
  for (let i = 1; i < v.length; i += 1) pairs.push([v[i - 1].lng, v[i].lng]);
  return pairs;
}

/** True when any prepared footprint polygon (cone, swath, timed wind areas) was stored in the shifted [0, 360) frame. */
export function footprintIsShifted(parsed) {
  const any = (g) => !!g?.polys?.some((p) => p.shifted);
  return (
    any(parsed?.cone) ||
    Object.values(parsed?.windSwath || {}).some(any) ||
    (parsed?.windByTime || []).some((w) => Object.values(w?.sets || {}).some(any))
  );
}
